import React, { useState, useEffect, useCallback } from 'react'
import DashboardLayout from '../../../layouts/Dashboard'
import { Col, Row, Button, Badge, Spinner, Image, Tooltip, OverlayTrigger } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { Plus, Warning, Pencil, UserCircle } from 'phosphor-react'
import api from '../../../services/api'
import { toast } from 'react-toastify'

import styles from './styles.module.css'
import { useLoading } from '../../../hooks/LoadingContext'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import SearchInput from '../../../components/SearchInput'

interface IAdmin {
  name: string
  user_id: string
  birth: Date
  state: string
  city: string
  user: {
    profilePicture: { url: string }
  }
  account_validated: boolean
}
const Admins: React.FC = () => {
  const [admins, setadmins] = useState<IAdmin[]>()
  const { isLoading, setIsLoading } = useLoading()
  const [hasMoreData, setHasMoreData] = useState<boolean>(true)
  const [search, setSearch] = useState('')
  const [lastSearch, setLastSearch] = useState('')

  const navigate = useNavigate()

  const loadData = useCallback(async () => {
    const isSearchDifferent = search !== lastSearch
    if (!hasMoreData && !isSearchDifferent) return

    setIsLoading(true)

    if (isSearchDifferent) {
      setLastSearch(search)
      setHasMoreData(true)
    }

    let apiUrl = '/admin/admins?profile_picture=true&limit=12&show_invalid=true'
    if (search) apiUrl += '&search=' + search

    const offset = admins && !isSearchDifferent ? admins.length : 0
    if (offset) apiUrl += '&offset=' + offset

    try {
      const { data } = await api.get(apiUrl)
      if (!data.length) {
        if (isSearchDifferent) setadmins(data)
        setHasMoreData(false)
      } else {
        if (!admins) setadmins(data)
        else setadmins(isSearchDifferent ? data : admins.concat(data))
      }
    } catch (err) {
      console.error(err)
      toast.error('Falha ao buscar os dados! Atualize a página.')
    }
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, admins, hasMoreData])

  useEffect(() => {
    if (!admins && hasMoreData) loadData()
  }, [loadData, hasMoreData, admins])

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop !==
          document.documentElement.offsetHeight ||
        isLoading
      ) {
        return
      }
      loadData()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoading, loadData])

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Administradores">
      <LoadingSpinner text="Buscando admins. Aguarde..." />
      <Row style={{ marginInline: 25 }}>
        <SearchInput
          placeholder="Digite o nome do admin"
          searchValue={search}
          setValue={setSearch}
          handleSearch={loadData}
          showOptions={false}
        />
        <Col
          style={{ display: 'flex', justifyContent: 'right' }}
          className="justify-content-md-end justify-content-center"
        >
          <Button
            className={styles.new_admin_button}
            onClick={() => navigate('/admin/admins/novo')}
          >
            <Plus
              style={{ marginRight: 4, marginBottom: 4 }}
              color="#fff"
              size={15}
              weight="bold"
            />
            Novo admin
          </Button>
        </Col>
      </Row>
      <Row style={{ marginInline: 30, display: 'flex', justifyContent: 'left' }}>
        {isLoading && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 40,
            }}
          >
            <Spinner
              animation="border"
              style={{
                color: '#053C5E',
                marginRight: 10,
              }}
            />
          </div>
        )}
        {!admins?.length && !isLoading && (
          <Badge
            bg=""
            text="white"
            style={{ textAlign: 'center', marginTop: 40, backgroundColor: '#053c5e' }}
          >
            <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" /> Nenhum
            administrador encontrado!
          </Badge>
        )}
        {admins &&
          admins.length > 0 &&
          admins?.map((admin, index) => (
            <Col key={index} style={{ padding: 8 }} className="col-12 col-md-6 col-lg-6 col-xl-4">
              <div className={styles.admin_card}>
                <Col md={5} style={{ alignSelf: 'center' }} className="col-5">
                  {admin.user.profilePicture ? (
                    <Image
                      style={{
                        objectFit: 'cover',
                        width: '100%',
                        aspectRatio: '1',
                        borderRadius: '50%',
                      }}
                      fluid={false}
                      src={admin.user.profilePicture.url}
                    />
                  ) : (
                    <UserCircle size={'100%'} />
                  )}
                </Col>
                <Col md={7} className={`col-7 ${styles.admin_info}`}>
                  <Row style={{ textAlign: 'center', margin: 0 }}>
                    <span className={styles.admin_name}>{admin.name}</span>
                    {!admin.account_validated && (
                      <div>
                        <Badge bg="danger" style={{ marginTop: 5, fontSize: 10 }}>
                          Conta não validada
                        </Badge>
                      </div>
                    )}
                  </Row>
                  <Row className={styles.btn_row}>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip>Editar administrador</Tooltip>}
                    >
                      <div
                        className={styles.edit_admin_button}
                        onClick={() => navigate(`/admin/admins/editar/${admin.user_id}`)}
                      >
                        <Pencil color="#053C5E" size={22} />
                      </div>
                    </OverlayTrigger>
                  </Row>
                </Col>
              </div>
            </Col>
          ))}
      </Row>
    </DashboardLayout>
  )
}

export default Admins
